import {
  faChevronDown,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Group, useMantineTheme } from '@mantine/core';
import classNames from 'classnames';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ContextMenu, Text } from '@feathr/components';

import {
  DEFAULT_MAX_ZOOM,
  DEFAULT_MIN_ZOOM,
  DEFAULT_ZOOM,
  DEFAULT_ZOOM_STEP,
  ZOOM_PRESETS,
} from '../PreviewPanel/PreviewPanel.utils';

import * as styles from './ZoomControls.css';

export interface IZoomControlsProps {
  className?: string;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onZoomToFit?: () => void;
  minZoom?: number;
  maxZoom?: number;
  step?: number;
}

function ZoomControls({
  className,
  zoom,
  onZoomChange,
  onZoomToFit,
  minZoom = DEFAULT_MIN_ZOOM,
  maxZoom = DEFAULT_MAX_ZOOM,
  step = DEFAULT_ZOOM_STEP,
}: Readonly<IZoomControlsProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const modKey = useMemo(() => (/mac/i.test(navigator.platform) ? '⌘' : 'Ctrl'), []);
  const isAtMin = zoom <= minZoom;
  const isAtMax = zoom >= maxZoom;

  function handleZoomOut(): void {
    onZoomChange(Math.max(minZoom, zoom - step));
  }

  function handleZoomIn(): void {
    onZoomChange(Math.min(maxZoom, zoom + step));
  }

  return (
    <Group className={classNames(styles.root, className)} gap={theme.spacing['1']}>
      <Button
        aria-label={t('Zoom out')}
        disabled={isAtMin}
        name={'zoom-out'}
        onClick={handleZoomOut}
        type={'icon-outlined'}
      >
        <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
      </Button>
      <Button
        aria-label={t('Zoom in')}
        disabled={isAtMax}
        name={'zoom-in'}
        onClick={handleZoomIn}
        type={'icon-outlined'}
      >
        <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
      </Button>
      <ContextMenu
        buttonClassName={styles.zoomButton}
        buttonText={`${zoom}%`}
        name={'zoom-menu'}
        position={'bottom-end'}
        suffix={<FontAwesomeIcon icon={faChevronDown} size={'xs'} />}
      >
        <ContextMenu.Item
          disabled={isAtMax}
          name={'zoom-in-menu-item'}
          onClick={handleZoomIn}
          suffix={
            <Text className={styles.shortcut} variant={'smallRegular'}>
              {modKey}+
            </Text>
          }
        >
          {t('Zoom in')}
        </ContextMenu.Item>
        <ContextMenu.Item
          disabled={isAtMin}
          name={'zoom-out-menu-item'}
          onClick={handleZoomOut}
          suffix={
            <Text className={styles.shortcut} variant={'smallRegular'}>
              {modKey}-
            </Text>
          }
        >
          {t('Zoom out')}
        </ContextMenu.Item>
        {onZoomToFit && (
          <ContextMenu.Item
            name={'zoom-to-fit-menu-item'}
            onClick={onZoomToFit}
            suffix={
              <Text className={styles.shortcut} variant={'smallRegular'}>
                {modKey}1
              </Text>
            }
          >
            {t('Zoom to fit')}
          </ContextMenu.Item>
        )}
        {ZOOM_PRESETS.map((preset) => (
          <ContextMenu.Item
            key={preset}
            name={`zoom-to-${preset}-menu-item`}
            onClick={() => onZoomChange(preset)}
            suffix={
              preset === DEFAULT_ZOOM ? (
                <Text className={styles.shortcut} variant={'smallRegular'}>
                  {modKey}0
                </Text>
              ) : undefined
            }
          >
            {t('Zoom to {{percent}}%', { percent: preset })}
          </ContextMenu.Item>
        ))}
      </ContextMenu>
    </Group>
  );
}

export default ZoomControls;
